import React, { useCallback } from 'react';
import Page from 'components/Page';
import Table from 'components/Table';
import { Button } from 'antd';
import useDataSource from 'hooks/useDataSource';
import { Route, Switch, Link, generatePath } from 'react-router-dom';
import { CheckOutlined, DeleteFilled, EditOutlined, PlusOutlined } from '@ant-design/icons';
import CurrencyFormat from 'components/CurrencyFormat';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import EditGoal, { eventTypes, matchAttributes } from './EditGoal';

const extraFields = `
  website {
    id
    matomoId
    matomoSettings {
      id
      currency
    }
    matomoGoals {
      id
      matomoGoalId
      matomoSiteId
      name
      description
      matchAttribute 
      pattern
      patternType
      revenue      
      funnelActivated
      multiAttributionEnabled
    }
  }
`;

const REMOVE_GOAL = gql`
  mutation($websiteId: ID!, $matomoGoalId: Int!) {
    deleteMatomoGoal(websiteId: $websiteId, matomoGoalId: $matomoGoalId)
  }
`;

const viewPath = '/data-sources/:dataSourceId/analytics/settings/goals/:matomoGoalId';

function renderGoalTriggers(text, record) {
  let triggerSentence = '';

  if (record.matchAttribute === 'manually') {
    triggerSentence = 'Manually';
  } else {
    triggerSentence += 'When visitor ';
    const eventType = eventTypes.find(t => t.value === record.matchAttribute);

    if (eventType) {
      triggerSentence += `send an event ${eventType.label}`;
    } else {
      const matchAttribute = matchAttributes.find(t => t.value === record.matchAttribute);
      if (matchAttribute) {
        triggerSentence += `${matchAttribute.label}`;
      }
    }
    if (record.matchAttribute !== 'stayForTime') {
      if (record.patternType === 'contains') {
        triggerSentence += ' containing ';
      } else if (record.patternType === 'exact') {
        triggerSentence += ' exactly ';
      } else {
        triggerSentence += ' matching the expression ';
      }
    }

    triggerSentence = (
      <>
        {record.matchAttribute !== 'stayForTime' && (
        <>
          {triggerSentence}
          <b>{record.pattern}</b>
        </>
        )}
        {record.matchAttribute === 'stayForTime' && (
        <>
          {' '}
          {triggerSentence}
          <b>
            {' '}
            {record.pattern}
            {' '}
            minutes
          </b>
        </>
        )}
      </>
    );
  }

  return triggerSentence;
}

const GoalsList = () => {
  const { dataSource, dataSourceId, loading, refetch } = useDataSource({ extraFields });
  const [removeGoal] = useMutation(REMOVE_GOAL);

  const websiteId = dataSource?.website.id;

  const onRemove = async (record) => {
    await removeGoal({ variables: { websiteId, matomoGoalId: record.matomoGoalId } });
    refetch();
  };

  const actionsMenuItems = useCallback((record) => [
    {
      key: 'delete',
      label: 'Delete',
      onClick: () => onRemove(record),
      danger: true,
      icon: <DeleteFilled />,
    },
  ], []);

  const actions = useCallback((record) => [
    <Link key="results" to={generatePath(viewPath, { dataSourceId, matomoGoalId: record.matomoGoalId })}>
      <Button icon={<EditOutlined />}>Edit</Button>
    </Link>,
  ], []);

  const columns = [
    {
      title: <>Goal&nbsp;Id</>,
      dataIndex: 'matomoGoalId',
      type: 'number',
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Goal is triggered when',
      dataIndex: 'goalTriggers',
      render: renderGoalTriggers,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      render: (revenue) => (revenue ? <CurrencyFormat value={revenue} currency={dataSource?.website.matomoSettings.currency} maximumFractionDigits={2} /> : '-'),
    },
    {
      title: 'Funnel',
      dataIndex: 'funnelActivated',
      render: (funnel) => (funnel ? <CheckOutlined style={{ color: 'green' }} /> : '-'),
      align: 'center',
    },
    {
      title: 'Attribution',
      dataIndex: 'multiAttributionEnabled',
      render: (attribution) => (attribution ? <CheckOutlined style={{ color: 'green' }} /> : '-'),
      align: 'center',
    },
  ];

  return (
    <Page>
      <Page.Header
        title="Manage Goals"
        extra={(
          <Link to={generatePath(viewPath, { dataSourceId, matomoGoalId: 'new' })}>
            <Button key="create-goal" icon={<PlusOutlined />} type="primary">Add goal</Button>
          </Link>
        )}
      />
      <Page.Content>
        <Table columns={columns} actions={actions} actionsMenuItems={actionsMenuItems} dataSource={dataSource?.website.matomoGoals} loading={loading} />
      </Page.Content>
    </Page>
  );
};

const ManageGoals = () => (
  <Switch>
    <Route path="/data-sources/:dataSourceId/analytics/settings/goals/:goalId">
      <EditGoal />
    </Route>
    <Route path="/data-sources/:dataSourceId/analytics/settings/goals">
      <GoalsList />
    </Route>
  </Switch>
);

export default ManageGoals;
